import { useCallback, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useDarkMode } from './hooks/useDarkMode'
import { isBoolean, usePersistedState } from './hooks/usePersistedState'
import type { Sort } from './lib/viewPrefs'
import type { RevealTarget } from './hooks/useRevealEntry'

// 'admin' is gone as a view: the administration surface lives inside the
// settings hub (RBAC-scoped segment) instead of a topbar destination.
export type AppView = 'home' | 'import' | 'stats' | 'settings'
const SORTS = new Set<Sort>(['created', 'clicks', 'recent', 'alpha', 'alpha_desc'])

function isSort(value: unknown): value is Sort {
  return SORTS.has(value as Sort)
}

function isGridDensity(value: unknown): value is 3 | 5 | 8 {
  return value === 3 || value === 5 || value === 8
}

export function useAppWorkspaceController() {
  const [viewState, setViewState] = useState<AppView>('home')
  // Which hub section the settings page should land on, plus a counter so
  // clicking "Profile" twice remounts SettingsPage back on the section even
  // when the view is already 'settings' (the page owns its section state).
  const [settingsJump, setSettingsJump] = useState<{ section: string; n: number } | null>(null)
  const [selectedTags, setSelectedTags] = useState<number[]>([])
  const [q, setQ] = useState('')
  const [sort, setSort] = usePersistedState<Sort>('foldex.sort', 'created', isSort)
  const [dark, setDark] = useDarkMode()
  const [sidebarCollapsed, setSidebarCollapsed] = usePersistedState('foldex.sidebar.collapsed', false, isBoolean)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [gridCols, setGridCols] = usePersistedState<3 | 5 | 8>('foldex.grid.cols', 5, isGridDensity)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [revealTarget, setRevealTarget] = useState<RevealTarget | null>(null)

  const toggleSidebar = useCallback(() => setSidebarCollapsed((collapsed) => !collapsed), [setSidebarCollapsed])
  const openMobileSidebar = useCallback(() => setMobileSidebarOpen(true), [])
  const closeMobileSidebar = useCallback(() => setMobileSidebarOpen(false), [])
  const openPalette = useCallback(() => setPaletteOpen(true), [])
  const closePalette = useCallback(() => setPaletteOpen(false), [])
  const requestReveal = useCallback((target: RevealTarget) => setRevealTarget(target), [])
  const clearReveal = useCallback(() => setRevealTarget(null), [])
  // Deep link into a settings-hub section (used by the topbar user menu).
  // Goes through setViewState, NOT the setView wrapper below: the wrapper
  // clears the jump so plain navigation lands on the overview, and calling
  // it here would erase the jump set on the previous line in the same batch.
  const openSettingsAt = useCallback((section: string) => {
    setSettingsJump((prev) => ({ section, n: (prev?.n ?? 0) + 1 }))
    setViewState('settings')
  }, [])
  // The plain gear button must land on the OVERVIEW, not on whatever section
  // the last deep link targeted — wrapping setView clears the jump so the
  // hub's remount key falls back to 'overview'.
  const setView = useCallback((v: AppView) => {
    if (v === 'settings') setSettingsJump(null)
    setViewState(v)
  }, [])
  const toggleTag = useCallback((id: number) => {
    setSelectedTags((selected) => (
      selected.includes(id) ? selected.filter((candidate) => candidate !== id) : [...selected, id]
    ))
    setMobileSidebarOpen(false)
  }, [])
  const clearTags = useCallback(() => {
    setSelectedTags([])
    setMobileSidebarOpen(false)
  }, [])

  useHotkeys('alt+k', (event) => {
    event.preventDefault()
    openPalette()
  })

  return {
    view: viewState,
    setView,
    settingsJump,
    openSettingsAt,
    selectedTags,
    q,
    setQ,
    sort,
    setSort,
    dark,
    setDark,
    sidebarCollapsed,
    toggleSidebar,
    mobileSidebarOpen,
    openMobileSidebar,
    closeMobileSidebar,
    gridCols,
    setGridCols,
    paletteOpen,
    openPalette,
    closePalette,
    revealTarget,
    requestReveal,
    clearReveal,
    toggleTag,
    clearTags,
  }
}

export type AppWorkspaceController = ReturnType<typeof useAppWorkspaceController>
